import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Select, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { organizationsApi } from "@/modules/organizations/api/organizations.api";
import type { AppRole } from "@/shared/config/navigation";
import { canManageObjectStructure } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { blocksApi } from "../api/blocks.api";
import { type BuildingCreatePayload, buildingsApi } from "../api/buildings.api";
import { filesApi } from "../api/files.api";
import { roomsApi } from "../api/rooms.api";
import { BuildingCard } from "../components/BuildingCard";
import { BuildingCreateModal } from "../components/BuildingCreateModal";
import { useObjectsStore } from "../store/objects.store";
import type { Building } from "../types/objects.types";

export function BuildingsPage() {
	const { role } = useOutletContext<{ role: AppRole }>();
	const navigate = useNavigate();
	const location = useLocation();
	const fromDashboard = (location.state as any)?.fromDashboard === true;
	const queryClient = useQueryClient();
	const setBuildings = useObjectsStore((state) => state.setBuildings);
	const [modalVisible, setModalVisible] = useState(false);
	const [editingBuildingId, setEditingBuildingId] = useState<string>();
	const canManageBuildings = canManageObjectStructure(role);
	const shouldChooseOrganization = role === "super";
	const [searchParams] = useSearchParams();
	const orgIdFromUrl = searchParams.get("orgId") || "all";
	const [selectedOrgId, setSelectedOrgId] = useState<string>(orgIdFromUrl);

	const { data: organizations = [], isFetching: isFetchingOrganizations } = useQuery({
		queryKey: ["organizations", "building-list"],
		queryFn: () => organizationsApi.getOrganizations({ limit: 100 }),
		enabled: shouldChooseOrganization,
	});

	useEffect(() => {
		if (orgIdFromUrl) {
			setSelectedOrgId(orgIdFromUrl);
		}
	}, [orgIdFromUrl]);

	useEffect(() => {
		if (shouldChooseOrganization && organizations.length > 0 && !selectedOrgId) {
			setSelectedOrgId("all");
		}
	}, [shouldChooseOrganization, organizations, selectedOrgId]);

	const {
		data: buildings = [],
		isLoading,
		isSuccess,
	} = useQuery({
		queryKey: ["buildings", selectedOrgId],
		queryFn: () =>
			buildingsApi.getBuildings({
				limit: 100,
				...(selectedOrgId && selectedOrgId !== "all" ? { orgId: selectedOrgId } : {}),
			}),
		enabled: !shouldChooseOrganization || !!selectedOrgId,
	});

	const { data: editingBuilding, isFetching: isFetchingBuilding } = useQuery({
		queryKey: ["buildings", editingBuildingId],
		queryFn: () => buildingsApi.getBuilding(editingBuildingId || ""),
		enabled: Boolean(editingBuildingId),
	});

	const buildingIds = useMemo(() => buildings.map((b) => b.id), [buildings]);

	const { data: allBlocks = [] } = useQuery({
		queryKey: ["blocks", "for-buildings", ...buildingIds.sort()],
		queryFn: async () => {
			const results = await Promise.all(
				buildingIds.map((id) => blocksApi.getBlocks({ buildingId: id, limit: 100 }).catch(() => []))
			);
			return results.flat();
		},
		enabled: buildingIds.length > 0,
	});

	const { data: allRooms = [] } = useQuery({
		queryKey: ["rooms", "for-buildings", ...buildingIds.sort()],
		queryFn: async () => {
			const results = await Promise.all(
				buildingIds.map((id) => roomsApi.getRooms({ buildingId: id, limit: 100 }).catch(() => []))
			);
			return results.flat();
		},
		enabled: buildingIds.length > 0,
	});

	const buildingsWithStats = useMemo(() => {
		return buildings.map((b) => {
			const buildingBlocks = allBlocks.filter((bl) => bl.buildingId === b.id);
			const buildingRooms = allRooms.filter((r) => r.buildingId === b.id);
			return {
				...b,
				blocksCount: buildingBlocks.length,
				totalCount: buildingRooms.length,
				soldCount: buildingRooms.filter((r) => r.status === "sotilgan").length,
				availableCount: buildingRooms.filter((r) => r.status === "sotuvda").length,
			};
		});
	}, [buildings, allBlocks, allRooms]);

	useEffect(() => {
		if (isSuccess) {
			setBuildings(buildingsWithStats);
		}
	}, [buildingsWithStats, isSuccess, setBuildings]);

	const createBuildingMutation = useMutation({
		mutationFn: buildingsApi.createBuilding,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["buildings"] });
			message.success("Bino muvaffaqiyatli qo'shildi!");
		},
	});

	const updateBuildingMutation = useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string;
			payload: Partial<BuildingCreatePayload> & { isActive?: boolean };
		}) => buildingsApi.updateBuilding(id, payload),
		onSuccess: async (_building, variables) => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["buildings"] }),
				queryClient.invalidateQueries({ queryKey: ["buildings", variables.id] }),
			]);
			message.success("Bino muvaffaqiyatli yangilandi!");
		},
	});

	const deleteBuildingMutation = useMutation({
		mutationFn: buildingsApi.deleteBuilding,
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["buildings"] });
			message.success("Bino muvaffaqiyatli o'chirildi!");
		},
		onError: (error) => {
			message.error(parseApiError(error));
		},
	});

	const handleSaveBuilding = async (
		newBuilding: BuildingCreatePayload & { isActive?: boolean },
		imageFile?: File
	) => {
		if (!canManageBuildings) {
			message.warning("Menejer bino strukturasini o'zgartira olmaydi.");
			return;
		}

		try {
			let image = newBuilding.image;
			if (imageFile) {
				const uploadedFile = await filesApi.uploadFile(imageFile, "image");
				image = uploadedFile.url;
			}

			if (editingBuildingId) {
				await updateBuildingMutation.mutateAsync({
					id: editingBuildingId,
					payload: { ...newBuilding, ...(image ? { image } : {}) },
				});
			} else {
				// Use the currently selected orgId as fallback for super admin if not selected in form
				const orgIdToUse = newBuilding.orgId || selectedOrgId || undefined;
				await createBuildingMutation.mutateAsync({ ...newBuilding, orgId: orgIdToUse, image });
			}
		} catch (error) {
			message.error(parseApiError(error));
			throw error;
		}
	};

	const handleEditBuilding = (building: Building) => {
		if (!canManageBuildings) {
			message.warning("Menejer bino strukturasini o'zgartira olmaydi.");
			return;
		}

		setEditingBuildingId(building.id);
		setModalVisible(true);
	};

	const handleDeleteBuilding = (building: Building) => {
		if (!canManageBuildings) {
			message.warning("Menejer bino strukturasini o'zgartira olmaydi.");
			return;
		}

		deleteBuildingMutation.mutate(building.id);
	};

	const handleCloseModal = () => {
		setModalVisible(false);
		setEditingBuildingId(undefined);
	};

	return (
		<div className="crm-buildings">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate("/dashboard")}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>Binolar</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						{shouldChooseOrganization
							? `${selectedOrgId === "all" ? "Barcha tashkilotlar" : organizations.find((o) => o.id === selectedOrgId)?.name || "Tashkilot"} · `
							: "Unicorp Group · "}
						{buildings.length} ta bino
					</p>
				</div>
				<div className="crm-objects-head-actions">
					{shouldChooseOrganization && organizations.length > 0 && (
						<Select
							value={selectedOrgId}
							onChange={setSelectedOrgId}
							options={[
								{ label: "Barchasi", value: "all" },
								...organizations.map((org) => ({
									label: org.name,
									value: org.id,
								})),
							]}
							style={{ minWidth: 200 }}
							placeholder="Tashkilotni tanlang"
						/>
					)}
					{canManageBuildings ? (
						<Button
							className="crm-primary-btn"
							icon={<PlusOutlined />}
							onClick={() => {
								setEditingBuildingId(undefined);
								setModalVisible(true);
							}}
						>
							Yangi bino
						</Button>
					) : null}
				</div>
			</div>

			<div className="crm-grid crm-buildings-grid">
				{isLoading ? (
					<div style={{ gridColumn: "1 / -1", padding: 60, textAlign: "center" }}>
						<Spin size="large" />
					</div>
				) : (
					buildingsWithStats.map((building) => (
						<BuildingCard
							key={building.id}
							building={building}
							isDeleting={
								deleteBuildingMutation.variables === building.id && deleteBuildingMutation.isPending
							}
							onDelete={handleDeleteBuilding}
							onEdit={handleEditBuilding}
							onDeleteImage={
								canManageBuildings
									? (b) => {
											updateBuildingMutation.mutate({
												id: b.id,
												payload: { image: "" },
											});
										}
									: undefined
							}
							userRole={role}
						/>
					))
				)}
			</div>

			<BuildingCreateModal
				initialBuilding={editingBuilding}
				isSaving={
					createBuildingMutation.isPending ||
					updateBuildingMutation.isPending ||
					isFetchingBuilding ||
					isFetchingOrganizations
				}
				organizations={organizations}
				requireOrganization={shouldChooseOrganization && !editingBuildingId}
				visible={modalVisible}
				onClose={handleCloseModal}
				onSave={handleSaveBuilding}
			/>
		</div>
	);
}
